import React, { useCallback, useEffect, useMemo, useState } from "react";
import { graphql, RelayEnvironmentProvider, useLazyLoadQuery } from "react-relay";
import { Redirect, Route, type RouteProps, Switch, useHistory, useLocation, useParams } from "react-router-dom";

import * as Sentry from "@sentry/react";
import { memoize, noop } from "lodash";
import invariant from "tiny-invariant";

import { type AppContextInterface, AppContextProvider } from "~/common/AppContext";
import { ThemeContextProvider } from "~/common/ThemeContext";
import { inCalendarMode } from "~/lib/utils";
import { ActionItemsPanel } from "~/meetings/SeriesSidePanel/ActionItems/ActionItemsPanel";
import Bookmarks from "~/meetings/SeriesSidePanel/Bookmarks/Bookmarks";
import { CommentsTab } from "~/meetings/SeriesSidePanel/Comments/CommentsPanel";
import { SalesforcePanel } from "~/meetings/SeriesSidePanel/CRMPanel/Salesforce/SalesforcePanel";
import PrivateNotes from "~/meetings/SeriesSidePanel/PrivateNotes/PrivateNotesPanel";
import { useAllowedSeriesSidePanelTabs } from "~/meetings/SeriesSidePanel/SeriesSidePanel";
import { environment } from "~/models";
import Nav, { type NavOption } from "~/nav";
import { ReactComponent as ActionItemsIconActive } from "~/nav/icons/action-items-filled.svg";
import { ReactComponent as ActionItemsIcon } from "~/nav/icons/action-items-outline.svg";
import { ReactComponent as AskFellowIconActive } from "~/nav/icons/ask-fellow-filled.svg";
import { ReactComponent as AskFellowIcon } from "~/nav/icons/ask-fellow-outline.svg";
import { CalendarOutline as MeetingsIcon, CalendarFilled as MeetingsIconActive } from "~/nav/icons/Calendar";
import Hubspot from "~/stream/SideBar/CRMPanel/Hubspot";
import {
	type CommentContext,
	StreamSideBarContext,
	type StreamSideBarContextType,
} from "~/stream/SideBar/SideBarContext";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import { ModalRoot } from "~/ui/modals";

import Notes from "./Notes";
import Tabs from "./Tabs";

import type { CompanionModeLandingPageQuery } from "~/__generated__/CompanionModeLandingPageQuery.graphql";
import type { CompanionModeQuery } from "~/__generated__/CompanionModeQuery.graphql";
import type { CompanionModeThroughEventGuidQuery } from "~/__generated__/CompanionModeThroughEventGuidQuery.graphql";
import type { CompanionModeThroughNoteIdQuery } from "~/__generated__/CompanionModeThroughNoteIdQuery.graphql";

import styles from "./CompanionMode.module.css";

const companionModeThroughNoteIdQuery = graphql`
	query CompanionModeThroughNoteIdQuery($noteId: ID!) {
		...AppAccount @relay(mask: false)
		...AppUser @relay(mask: false)

		viewer {
			settings {
				theme
			}

			hasAdminAccess
			hasAccessToCopilot
			uxSimplificationEnabled
			...Bookmarks_viewer
			...SeriesSidePanel_allowedTabs_viewer
			...Notes_viewer
		}

		note: node(id: $noteId) {
			__typename
			... on Note {
				id
				stream {
					id
					...SeriesSidePanel_allowedTabs_stream
					...Bookmarks_stream
				}

				...TabsCompanionMode_note
				...Notes_note
			}
		}
	}
`;

function warnAboutEmptyContext(functionName: string) {
	const message = `${functionName} was called but is currently noop.`;
	return () => {
		console.warn(message);
		Sentry.captureMessage(message, {
			level: "warning",
		});
	};
}

function RouteIfAllowed(props: RouteProps & { isAllowed: boolean }) {
	const params = useParams<{ noteId: string }>();

	if (!props.isAllowed) return <Redirect to={`/companion-mode/${params.noteId}/notes/`} />;

	return <Route {...props} />;
}

function CompanionModeThroughNoteId() {
	const { noteId } = useParams<{ noteId: string }>();

	invariant(noteId, "noteId should exist when rendering CompanionModeThroughNoteId");

	const { user, account, viewer, note } = useLazyLoadQuery<CompanionModeThroughNoteIdQuery>(
		companionModeThroughNoteIdQuery,
		{
			noteId,
		},
	);

	const allowedTabs = useAllowedSeriesSidePanelTabs(viewer, note && note.__typename === "Note" ? note.stream : null);

	const appContextValue = React.useMemo<AppContextInterface>(
		() => ({
			user: {
				...user,
				hasAdminAccess: viewer.hasAdminAccess,
				hasAccessToCopilot: viewer.hasAccessToCopilot,
				uxSimplificationEnabled: viewer.uxSimplificationEnabled,
			},
			account,
			handleUtmAnalytics: noop,
			setSidebar: noop,
			getSidebar: () => null,
			closeSidebar: noop,
			openSettingsModal: noop,
		}),
		[user, viewer.hasAdminAccess, viewer.hasAccessToCopilot, viewer.uxSimplificationEnabled, account],
	);

	const [commentContext, _setCommentContext] = useState<CommentContext | undefined>(undefined);
	const history = useHistory();
	const goToCommentsTab = useCallback(
		(commentContext: CommentContext | undefined) => {
			if (!commentContext) {
				_setCommentContext(undefined);
				history.push(`/companion-mode/${noteId}/notes/`);
				return;
			}

			_setCommentContext(commentContext);
			history.push(`/companion-mode/${noteId}/comments/${commentContext.threadKey}`);
		},
		[history, noteId],
	);

	// TODO: We should make a new context for the new UI… most of this isn't needed or is different in the new UI.
	const sidebarContextValue = useMemo<StreamSideBarContextType>(
		() => ({
			setCommentContext: goToCommentsTab,

			// These are only included because we have them here
			currentNoteId: noteId,
			commentContext,

			// These aren't needed or are otherwise not available…
			setCurrentNoteId: warnAboutEmptyContext("setCurrentNoteId"),
			agendaBuilderCurrentNoteId: null,
			setAgendaBuilderCurrentNoteId: warnAboutEmptyContext("setAgendaBuilderCurrentNoteId"),
			agendaBuilderModeRegenerateKey: 0,
			onGenerateAgendaBuilder: warnAboutEmptyContext("onGenerateAgendaBuilder"),
			doneGeneratingAIHeaders: true,
			setDoneGeneratingAIHeaders: warnAboutEmptyContext("setDoneGeneratingAIHeaders"),
			askCopilotContext: undefined,
			setAskCopilotContext: warnAboutEmptyContext("setAskCopilotContext"),
			meetingBriefContext: undefined,
			setMeetingBriefContext: warnAboutEmptyContext("setMeetingBriefContext"),
		}),
		[commentContext, goToCommentsTab, noteId],
	);

	const makeRenderFnFor = useCallback((content: React.ReactNode) => memoize(() => content), []);

	if (!note || note.__typename !== "Note" || !note.stream) {
		// TODO: Return 404, or maybe redirect to calendar?
		return null;
	}

	return (
		<AppContextProvider value={appContextValue}>
			<ThemeContextProvider theme={viewer.settings.theme}>
				<ModalRoot>
					<StreamSideBarContext.Provider value={sidebarContextValue}>
						<div className={styles.meetingView}>
							<Tabs viewer={viewer} stream={note.stream} noteKey={note} />
							<div className={styles.tabContent}>
								<Route
									path="/companion-mode/:noteId/notes/"
									render={makeRenderFnFor(<Notes viewer={viewer} note={note} />)}
								/>
								<Route
									path="/companion-mode/:noteId/private-notes/"
									render={makeRenderFnFor(<PrivateNotes streamId={note.stream.id} noteId={note.id} />)}
								/>
								<Route
									path="/companion-mode/:noteId/action-items/"
									render={makeRenderFnFor(<ActionItemsPanel streamId={note.stream.id} />)}
								/>
								<Route
									path="/companion-mode/:noteId/bookmarks/"
									render={makeRenderFnFor(<Bookmarks stream={note.stream} viewer={viewer} />)}
								/>
								<RouteIfAllowed
									isAllowed={allowedTabs.hubspot}
									path="/companion-mode/:noteId/hubspot/"
									render={makeRenderFnFor(
										<Hubspot streamId={note.stream.id} noteId={note.id} hasAccessToHubspotIntegration={true} />,
									)}
								/>
								<RouteIfAllowed
									isAllowed={allowedTabs.salesforce}
									path="/companion-mode/:noteId/salesforce/"
									render={makeRenderFnFor(
										<SalesforcePanel
											streamId={note.stream.id}
											noteId={note.id}
											hasAccessToSalesforceIntegration={true}
											enhancedNotesEnabled={allowedTabs.salesforceEnhancedNotesEnabled}
										/>,
									)}
								/>
								<Route path="/companion-mode/:noteId/comments/:threadKey" render={makeRenderFnFor(<CommentsTab />)} />
							</div>
						</div>
					</StreamSideBarContext.Provider>
				</ModalRoot>
			</ThemeContextProvider>
		</AppContextProvider>
	);
}

function CompanionModeThroughEventGuid() {
	const location = useLocation();
	const { eventGuid } = useParams<{ eventGuid: string }>();
	const inCalendarParam = new URLSearchParams(location.search).get("in_calendar");

	invariant(eventGuid, "eventGuid should exist when rendering CompanionModeThroughEventGuid");

	const data = useLazyLoadQuery<CompanionModeThroughEventGuidQuery>(
		graphql`
			query CompanionModeThroughEventGuidQuery($eventGuid: String!) {
				viewer {
					uxSimplificationEnabled
				}
				event: eventByGuid(guid: $eventGuid) {
					id
					note(ifNotExists: CREATE) {
						id
					}
				}
			}
		`,
		{
			eventGuid,
		},
	);

	// At the time of writing, this path needs to be feature flagged. Since we can't check the feature flag in the
	// extension, we have to check here.
	if (inCalendarMode && !data.viewer?.uxSimplificationEnabled) {
		// TODO: This redirect could be on the backend
		return <Redirect to={`/meetings/${eventGuid}?${inCalendarParam ? `in_calendar=${inCalendarParam}` : ""}`} />;
	}

	if (!data.event) {
		return <div>Somehow this event could not be found. (TODO: how? I don&apos;t think this can happen)</div>;
	}

	if (!data.event.note?.id) {
		return <div>Oops, we couldn&apos;t create an agenda for this meeting (TODO: Click here to create one maybe?)</div>;
	}

	// TODO: this redirect could be on the backend
	return <Redirect to={`/companion-mode/${data.event.note.id}/notes/`} />;
}

function CompanionModeLandingPage() {
	const location = useLocation();
	const inCalendarParam = new URLSearchParams(location.search).get("in_calendar");

	const data = useLazyLoadQuery<CompanionModeLandingPageQuery>(
		graphql`
			query CompanionModeLandingPageQuery {
				viewer {
					uxSimplificationEnabled
				}
			}
		`,
		{},
	);

	// At the time of writing, this path to be feature flagged. Since we can't check the feature flag in the
	// extension, we have to check here.
	if (!data.viewer?.uxSimplificationEnabled) {
		// TODO: This redirect could be on the backend
		return <Redirect to={`/action-items/?${inCalendarParam ? `in_calendar=${inCalendarParam}` : ""}`} />;
	}

	// TODO: Maybe this should stay empty and just open one of the panels
	return <div className="flex-1 flex items-center justify-center">Select an event to get started.</div>;
}

function CompanionMode() {
	const queryResponse = useLazyLoadQuery<CompanionModeQuery>(
		graphql`
			query CompanionModeQuery {
				...nav_viewer
			}
		`,
		{},
	);

	const navOptions = useMemo<NavOption[]>(
		() => [
			{
				icon: {
					inactive: <MeetingsIcon />,
					active: <MeetingsIconActive />,
				},
				panelKey: "meetings",
				label: "Meetings",
			},
			{
				icon: {
					inactive: <AskFellowIcon />,
					active: <AskFellowIconActive />,
				},
				panelKey: "ask-fellow",
				label: "Ask Fellow",
				customActiveIconClass: styles.askFellowActiveWrapper,
				customActiveLabelClass: styles.askFellowActiveLabel,
			},
			{
				icon: {
					inactive: <ActionItemsIcon />,
					active: <ActionItemsIconActive />,
				},
				panelKey: "action-items",
				label: "Action items",
			},
		],
		[],
	);

	return (
		<div className="flex-1 flex flex-col h-full">
			<React.Suspense
				fallback={
					<div className="flex-1 flex items-center justify-center relative">
						<LoadingSpinner dimmer />
					</div>
				}
			>
				<Switch>
					<Route exact path="/companion-mode/" component={CompanionModeLandingPage} />
					<Route path="/companion-mode/event/:eventGuid/" component={CompanionModeThroughEventGuid} />
					<Route path="/companion-mode/:noteId/:tab?/" component={CompanionModeThroughNoteId} />
				</Switch>
			</React.Suspense>
			{/**
			 * At the time of writing, the nav tries to render the full left side bar if the screen is large enough.
			 * In a perfect world, the bottom nav and left nav would be separate components, and we'd only ever render the
			 * bottom nav here.
			 */}
			{/* TODO: I think this needs to be rendered in Zoom */}
			{inCalendarMode && <Nav query={queryResponse} hideInvite={true} showUpgradeLink={false} items={navOptions} />}
		</div>
	);
}

export default function HotReloadingRelayWrapper() {
	const history = useHistory();
	const onBrowserExtensionMessage = useCallback(
		(event: MessageEvent) => {
			if (
				event.data.type === "BROWSER_EXTENSION_ACTION" &&
				event.data.action === "navigateToMeeting" &&
				event.data.path
			) {
				return history.replace(event.data.path);
			}

			if (event.data.type === "BROWSER_EXTENSION_ACTION" && event.data.action === "clearSessionStorage") {
				return window.sessionStorage.clear();
			}
		},
		[history],
	);

	useEffect(() => {
		if (inCalendarMode) {
			window.addEventListener("message", onBrowserExtensionMessage);
			window.parent.postMessage({ type: "FRAME_LISTENER_READY" }, "*");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<RelayEnvironmentProvider environment={environment}>
			<CompanionMode />
		</RelayEnvironmentProvider>
	);
}
